import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function BlogIndexPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Blog · Recetas y guías de cortes",
    description: "Recetas, técnicas de parrilla y guías de cortes premium estilo norteño. Aprende del compare.",
  });

  useEffect(() => {
    const url = filter === "all" ? "/blog/posts" : `/blog/posts?category=${encodeURIComponent(filter)}`;
    Promise.all([
      api.get(url).then((r) => r.data).catch(() => []),
      api.get("/blog/categories").then((r) => r.data).catch(() => []),
    ]).then(([p, c]) => {
      setPosts(p);
      setCategories(c);
      setLoading(false);
    });
  }, [filter]);

  return (
    <div className="bg-arre-black min-h-screen py-16 px-6 md:px-12" data-testid="blog-index">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Blog</div>
        <h1 className="font-display text-6xl md:text-8xl tracking-wider uppercase text-white leading-[0.85]">
          Recetas &<br /><span className="text-arre-red italic font-serif-display normal-case tracking-tight">guías</span>
        </h1>
        <p className="text-white/60 max-w-xl mt-4 mb-10">
          Aprende a sacar el máximo a cada corte. Tips, técnicas y recetas del norte mexicano.
        </p>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>Todos</FilterPill>
            {categories.map((c) => (
              <FilterPill key={c.name} active={filter === c.name} onClick={() => setFilter(c.name)}>
                {c.name} ({c.count})
              </FilterPill>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-arre-surface border border-arre-border animate-pulse">
                <div className="aspect-[16/10]" />
                <div className="p-5 space-y-2">
                  <div className="h-3 bg-white/5 w-1/4" />
                  <div className="h-6 bg-white/10" />
                  <div className="h-4 bg-white/10 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-arre-surface border border-arre-border p-12 text-center">
            <div className="font-display text-3xl tracking-wider uppercase text-white/60">Próximamente</div>
            <p className="text-white/40 mt-2">Estamos preparando contenido épico, compare.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group bg-arre-surface border border-arre-border hover:border-arre-red transition-colors"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-arre-black">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-arre-surface to-arre-black">
                      <div className="font-display text-arre-red/30 text-6xl">⟡</div>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-arre-red mb-2">{p.category}</div>
                  <h3 className="font-display text-2xl tracking-wider uppercase text-white leading-tight group-hover:text-arre-red transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-white/60 text-sm mt-2 line-clamp-2">{p.excerpt}</p>
                  <div className="flex items-center justify-between mt-4 text-[11px] text-white/40">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(p.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                    <ArrowRight className="w-4 h-4 text-arre-red group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-xs tracking-widest uppercase border transition ${
        active ? "bg-arre-red border-arre-red text-white" : "bg-arre-surface border-arre-border text-white/70 hover:border-white/40"
      }`}
    >
      {children}
    </button>
  );
}
